import clsx from 'clsx';
import type {
  MetricHistoryEntry,
  ModelSchema,
  OutputThresholds,
} from '@arcanum-hq/shared-types';
import { PerformanceTile, SPARKLINE_KPI_ALLOWLIST } from './PerformanceTile';
import type { ThresholdFormat } from './ThresholdBand';

/**
 * `<HeroMetricsHeader>` — 5-cell KPI strip rendered at the top of the
 * Builder Signal + Model tabs (NOT Workbench). Phase 6 Session 3 MVP
 * (2026-05-08): composes the existing `<PerformanceTile>` primitive
 * (Phase 3) — chip + label + value + delta + 5-segment threshold band.
 *
 * The component is shared between the Signal + Model tabs (per design.md
 * Q1.3 lock 2026-04-28). Session 4 introduces a richer `<HeroMetricCell>`
 * primitive set with sparkline + signal-classification dot + variant-aware
 * formatting; it'll swap into this header without changing the call sites.
 *
 * Metric selection priority:
 *   1. `modelSchema.displayConfig.keyMetricNames[0..4]` — admin-curated.
 *   2. The 4 names in `SPARKLINE_KPI_ALLOWLIST` — fallback when the model
 *      hasn't been configured by an admin yet.
 *   3. The first 5 outputs by `displayName` — last-resort for brand-new
 *      models with neither displayConfig nor a name overlap with the
 *      allowlist.
 *
 * Empty states: when the conversation has no metricHistory or the engine
 * hasn't computed values yet, each tile renders its `—` placeholder
 * (PerformanceTile's null-value branch). The header strip itself never
 * unmounts on cold-load — it's the visual anchor of the Signal/Model tabs.
 */

const DATA_TYPE_TO_THRESHOLD_FORMAT: Record<string, ThresholdFormat> = {
  percentage: 'percentage',
  currency: 'currency',
  currency_per_sf: 'currency',
  currency_per_unit_year: 'currency',
  // Multipliers (e.g. EquityMultiple, DSCR, CoC) all use 'multiplier' for
  // the threshold band's "1.50x" / "0.50pp" delta formatting.
  decimal: 'multiplier',
  integer: 'multiplier',
  months: 'multiplier',
  years: 'multiplier',
};

interface HeroOutput {
  name: string;
  displayName: string;
  format: ThresholdFormat;
  thresholds?: OutputThresholds;
}

function pickHeroNames(modelSchema: ModelSchema | null): string[] {
  if (!modelSchema) return [];
  const configured = modelSchema.displayConfig?.keyMetricNames ?? [];
  if (configured.length >= 5) return configured.slice(0, 5);

  const allowlistOrder = Array.from(SPARKLINE_KPI_ALLOWLIST);
  const merged: string[] = [];
  // Configured names come first (preserves admin priority); then allowlist
  // names that the schema actually exposes; then fill from the schema's
  // remaining outputs in declaration order.
  for (const name of [...configured, ...allowlistOrder]) {
    if (merged.length >= 5) break;
    if (!merged.includes(name)) merged.push(name);
  }
  if (merged.length < 5) {
    for (const out of modelSchema.outputs ?? []) {
      if (merged.length >= 5) break;
      if (!merged.includes(out.name)) merged.push(out.name);
    }
  }
  return merged.slice(0, 5);
}

function resolveOutput(
  name: string,
  modelSchema: ModelSchema | null,
): HeroOutput | null {
  if (!modelSchema) return null;
  const out = (modelSchema.outputs ?? []).find((o) => o.name === name);
  if (!out) return null;
  const format = DATA_TYPE_TO_THRESHOLD_FORMAT[out.dataType] ?? 'multiplier';
  // outputOverrides[name].thresholds carries the band when the admin has
  // configured one — same shape `<PerformanceTile>` consumes elsewhere in
  // the panel surfaces.
  const override = modelSchema.displayConfig?.outputOverrides?.[name];
  return {
    name: out.name,
    displayName: out.displayName,
    format,
    thresholds: override?.thresholds,
  };
}

export interface HeroMetricsHeaderProps {
  /** Active conversation id; used as a stable React key bridge. */
  conversationId: string;
  /** Schema for the conversation's pinned model (resolved via `useModel`). */
  modelSchema: ModelSchema | null;
  /** Live engine metrics — namedRange → value (after each `set_inputs` calculate). */
  liveMetrics: Record<string, number>;
  /** Per-metric ring buffer from the conversation's agent_state. */
  metricHistory: Record<string, MetricHistoryEntry[]> | null;
  className?: string;
}

export function HeroMetricsHeader({
  conversationId,
  modelSchema,
  liveMetrics,
  metricHistory,
  className,
}: HeroMetricsHeaderProps) {
  const heroNames = pickHeroNames(modelSchema);

  return (
    <div
      data-testid="hero-metrics-header"
      data-conversation-id={conversationId}
      className={clsx(
        'grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 px-5 py-3 border-b border-panel-border bg-bg shrink-0',
        className,
      )}
    >
      {heroNames.map((name) => {
        const resolved = resolveOutput(name, modelSchema);
        const value =
          name in liveMetrics && Number.isFinite(liveMetrics[name])
            ? liveMetrics[name]
            : null;
        const history = metricHistory?.[name];
        return (
          <PerformanceTile
            key={name}
            name={name}
            label={resolved?.displayName ?? name}
            value={value}
            format={resolved?.format ?? 'multiplier'}
            thresholds={resolved?.thresholds}
            history={history}
          />
        );
      })}
      {heroNames.length === 0 && (
        <div
          data-testid="hero-metrics-empty"
          className="col-span-full text-center py-2 text-xs font-mono text-text-muted"
        >
          No model data yet.
        </div>
      )}
    </div>
  );
}
